// 2026-06-28 - FormuLearning - Step 1 RLD 대조약 비교(별도 창 팝업)
// 파일 위치: frontend/src/features/step1/rldCompare.ts
// 인페이지 모달 대신 별도 브라우저 창으로 비교 매트릭스를 띄운다(항목이 많아 전체 화면 가독성 확보).
// 2026-09-23 수정 - 이슈 #73 P4: 창 제목 «대조약 비교» → «허가 품목 비교»(Approved Drug 전환, `Q-260923-01` 잠정).

// 비교 매트릭스 세로축(항목). 상세 패널과 동일한 11종 항목 집합으로 정렬(260628 회의 피드백).
// Product Name·Manufacturer는 각 컬럼 헤더(제품명/제조사)로 표시하므로 행에서는 제외.
const COMPARE_FIELDS: [string, string][] = [
  ['main_ingredient_detail', 'Active Ingredient (Content)'],
  ['appearance', 'Appearance'],
  ['weight_mg', 'Total Weight'],
  ['insurance_price', 'Insurance Drug Price'],
  ['packaging', 'Packing Type'],
  ['excipients', 'Excipients'],
  ['storage', 'Storage Conditions'],
  ['dosage_usage', 'Dosage / Administration'],
  ['efficacy', 'Efficacy'],
  ['shelf_life', 'Shelf Life'],
]

function cellValue(rld: Record<string, unknown>, key: string): string | null {
  const v = rld[key]
  if (v === null || v === undefined || v === '') return null
  if (key === 'weight_mg') return `${v} mg`
  return String(v)
}

// 행의 값들이 컬럼 간 서로 다르면 true(차이 강조용).
// 누락(—)도 하나의 값으로 보고 모든 컬럼을 비교(한쪽만 값이 있어도 차이로 인정).
// 공백·줄바꿈은 정규화해 형식 차이로 인한 오탐을 방지.
function rowHasDiff(rlds: Record<string, unknown>[], key: string): boolean {
  const norm = (r: Record<string, unknown>) => {
    const v = cellValue(r, key)
    return v === null ? '—' : v.trim().replace(/\s+/g, ' ')
  }
  return new Set(rlds.map(norm)).size > 1
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string)
}

interface Theme {
  bg: string
  panel: string
  text: string
  textPrimary: string
  secondary: string
  muted: string
  border: string
  accent: string
  diffBg: string
  imgBg: string
}

// 메인 문서(<html data-theme>)에 적용된 CSS 변수를 읽어 팝업 창을 동일 테마로 맞춘다.
// mixed 모드는 본문이 라이트이므로 루트(documentElement) 기준으로 읽으면 본문 테마와 일치.
function readTheme(): Theme {
  const cs = getComputedStyle(document.documentElement)
  const v = (name: string, fb: string) => cs.getPropertyValue(name).trim() || fb
  const themeAttr = document.documentElement.getAttribute('data-theme')
  const isDark = themeAttr !== 'light' && themeAttr !== 'mixed'
  return {
    bg: v('--color-bg-base', isDark ? '#202124' : '#ffffff'),
    panel: v('--color-bg-panel', isDark ? '#26282c' : '#f3f4f6'),
    text: v('--color-text-body', isDark ? '#e8eaed' : '#232529'),
    textPrimary: v('--color-text-primary', isDark ? '#f1f3f4' : '#17181b'),
    secondary: v('--color-text-secondary', isDark ? '#9aa0a6' : '#5c6066'),
    muted: v('--color-text-muted', isDark ? '#c4c7c5' : '#45484d'),
    border: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
    accent: v('--color-accent-purple', isDark ? '#9b8afb' : '#6c4ed6'),
    diffBg: isDark ? 'rgba(255,184,77,0.14)' : '#fff7ed',
    imgBg: '#ffffff',
  }
}

function buildHtml(rlds: Record<string, unknown>[], t: Theme): string {
  // 주성분·총중량은 값이 없어도 항상 노출(제형설계 필수 수치 — 요청 260629). 나머지는 값이 하나라도 있을 때만.
  const ALWAYS = new Set(['main_ingredient_detail', 'weight_mg'])
  const visibleFields = COMPARE_FIELDS.filter(([key]) => ALWAYS.has(key) || rlds.some((r) => cellValue(r, key) !== null))
  const hasImage = rlds.some((r) => cellValue(r, 'image_url') !== null)

  const headCols = rlds
    .map((r) => {
      const prod = esc(cellValue(r, 'product_name') ?? '—')
      const maker = cellValue(r, 'maker')
      return `<th><div class="prod">${prod}</div>${maker ? `<div class="maker">${esc(maker)}</div>` : ''}</th>`
    })
    .join('')

  const imageRow = hasImage
    ? `<tr><th>이미지</th>${rlds
        .map((r) => {
          const img = cellValue(r, 'image_url')
          return `<td>${img ? `<img src="${esc(img)}" alt="" onerror="this.style.display='none'">` : '<span class="muted">—</span>'}</td>`
        })
        .join('')}</tr>`
    : ''

  const fieldRows = visibleFields
    .map(([key, label]) => {
      const diff = rowHasDiff(rlds, key)
      const cells = rlds
        .map((r) => {
          const v = cellValue(r, key)
          return `<td>${v ? esc(v) : '<span class="muted">—</span>'}</td>`
        })
        .join('')
      return `<tr class="${diff ? 'diff' : ''}"><th>${esc(label)}</th>${cells}</tr>`
    })
    .join('')

  return `<!doctype html>
<html lang="ko"><head><meta charset="utf-8">
<title>허가 품목 비교 (${rlds.length}개)</title>
<style>
  body{font-family:system-ui,-apple-system,'Apple SD Gothic Neo','Malgun Gothic',sans-serif;margin:0;padding:16px;background:${t.bg};color:${t.text}}
  h1{font-size:15px;margin:0 0 12px;color:${t.textPrimary}}
  table{border-collapse:separate;border-spacing:0;width:100%;font-size:12px}
  th,td{border-bottom:1px solid ${t.border};border-right:1px solid ${t.border};padding:8px 10px;text-align:left;vertical-align:top}
  thead th{position:sticky;top:0;background:${t.panel};z-index:2;border-top:1px solid ${t.border}}
  tbody th{position:sticky;left:0;background:${t.panel};font-weight:600;color:${t.secondary};white-space:nowrap;min-width:130px;border-left:1px solid ${t.border}}
  thead th:first-child{left:0;z-index:3;border-left:1px solid ${t.border}}
  tr.diff th,tr.diff td{background:${t.diffBg}}
  tr.diff th{border-left:3px solid ${t.accent}}
  .prod{font-weight:700;color:${t.textPrimary}}
  .maker{font-size:11px;color:${t.secondary};font-weight:400;margin-top:2px}
  img{max-height:120px;background:${t.imgBg};border:1px solid ${t.border};border-radius:4px;padding:2px;object-fit:contain}
  .muted{color:${t.muted};opacity:0.7}
  .note{margin-top:12px;font-size:11px;color:${t.muted}}
</style></head>
<body>
  <h1>허가 품목 비교 <span style="color:${t.secondary};font-weight:400">(${rlds.length}개)</span></h1>
  <table>
    <thead><tr><th>항목</th>${headCols}</tr></thead>
    <tbody>${imageRow}${fieldRows}</tbody>
  </table>
  <p class="note">값이 서로 다른 항목은 강조 표시됩니다. 상세 필드는 조회한 품목에만 제공될 수 있습니다.</p>
</body></html>`
}

/** 선택한 대조약들을 별도 창으로 비교 표시. 팝업 차단 시 안내. */
export function openRldCompareWindow(rlds: Record<string, unknown>[]): void {
  const win = window.open('', '_blank', 'width=1100,height=800,scrollbars=yes,resizable=yes')
  if (!win) {
    alert('팝업이 차단되었습니다. 브라우저에서 이 사이트의 팝업을 허용한 뒤 다시 시도하세요.')
    return
  }
  win.document.open()
  win.document.write(buildHtml(rlds, readTheme()))
  win.document.close()
}
